/**
 * Composer — the question input + Send/Cancel control for the ideation chat.
 *
 * Presentational: holds only the draft text locally; the assembled-context
 * scope/depth and streaming state are owned by the parent
 * ({@link IdeationWorkspace}). While `disabled` (a stream is in flight) the
 * Send button is replaced by a Cancel button that calls `onCancel`, so a
 * long answer can be aborted without losing the draft.
 *
 * Enter submits; Shift+Enter inserts a newline (standard chat affordance).
 *
 * Attach flow (optional): when `onAttach` is supplied a paperclip button +
 * hidden file input appear. Selecting a file calls `onAttach(file)`. While the
 * upload/attach is in flight `attaching` shows a spinner chip; once settled
 * `attachedName` renders a filename chip confirming what is grounding the chat.
 * Callers that don't supply `onAttach` see no attach UI (existing callers are
 * unaffected).
 */

import { Button, TextArea } from "@l30/ui";
import { FileText, Loader2, Paperclip, Pin, Send, Sparkles, Square } from "lucide-react";
import { useRef, useState } from "react";

interface ComposerProps {
  /** True while a stream is in flight → swaps Send for Cancel, blocks submit. */
  disabled: boolean;
  onSubmit: (question: string) => void;
  onCancel: () => void;
  onExtract?: () => void;
  canExtract?: boolean;
  /** Pin the last answer as an Insight node. Omitted → no button. */
  onPin?: () => void;
  /** Draft the last answer as an editable document. Omitted → no button. */
  onDraftDoc?: () => void;
  /** Enables Pin + Save-as-doc (a durable answer exists and no stream runs). */
  canPinOrDraft?: boolean;
  /**
   * Called with the selected File when the user picks a file to attach.
   * Omitting this prop hides the entire attach UI (existing callers unchanged).
   */
  onAttach?: (file: File) => void;
  /** True while the upload + attach mutation is in flight → spinner chip. */
  attaching?: boolean;
  /** Filename of the most recently attached file → shown as a confirmation chip. */
  attachedName?: string | null;
  /** Seed the draft without submitting. Used when arriving from search. */
  initialValue?: string;
}

export function Composer({
  disabled,
  onSubmit,
  onCancel,
  onExtract,
  canExtract,
  onPin,
  onDraftDoc,
  canPinOrDraft,
  onAttach,
  attaching,
  attachedName,
  initialValue,
}: ComposerProps) {
  const [question, setQuestion] = useState(initialValue ?? "");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const submit = () => {
    const q = question.trim();
    if (!q || disabled) return;
    onSubmit(q);
    setQuestion("");
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && onAttach) {
      onAttach(file);
      // Reset so the same file can be re-attached if needed.
      e.target.value = "";
    }
  };

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      {/* Attach status chips — only shown when the attach feature is wired in. */}
      {onAttach ? (
        <div className="flex items-center gap-2">
          {attaching ? (
            <span
              role="status"
              className="inline-flex items-center gap-1 rounded-full bg-border-muted px-2 py-0.5 text-xs text-fg-muted"
            >
              <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
              Attaching…
            </span>
          ) : attachedName ? (
            <span className="inline-flex items-center gap-1 rounded-full bg-border-muted px-2 py-0.5 text-xs text-fg-muted">
              <Paperclip className="h-3 w-3" aria-hidden="true" />
              {attachedName}
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="flex items-end gap-2">
        <div className="min-w-0 flex-1">
          <TextArea
            aria-label="Ask a question"
            placeholder="Ask a question…"
            value={question}
            rows={2}
            disabled={disabled}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
          />
        </div>
        {onAttach ? (
          <>
            {/* Hidden native file input — triggered by the paperclip button. */}
            <input
              ref={fileInputRef}
              type="file"
              aria-label="Attach a file"
              accept=".docx,.xlsx,.pptx,.pdf,.md,.txt,.csv,.json,image/*"
              className="sr-only"
              onChange={handleFileChange}
            />
            <Button
              type="button"
              variant="secondary"
              className="gap-2"
              disabled={attaching || disabled}
              onClick={() => fileInputRef.current?.click()}
            >
              <Paperclip className="h-4 w-4" aria-hidden="true" />
              Attach
            </Button>
          </>
        ) : null}
        {onPin ? (
          <Button
            type="button"
            variant="secondary"
            className="gap-2"
            disabled={!canPinOrDraft || disabled}
            onClick={onPin}
          >
            <Pin className="h-4 w-4" aria-hidden="true" />
            Pin as Insight
          </Button>
        ) : null}
        {onDraftDoc ? (
          <Button
            type="button"
            variant="secondary"
            className="gap-2"
            disabled={!canPinOrDraft || disabled}
            onClick={onDraftDoc}
          >
            <FileText className="h-4 w-4" aria-hidden="true" />
            Save as doc
          </Button>
        ) : null}
        {onExtract ? (
          <Button
            type="button"
            variant="secondary"
            className="gap-2"
            disabled={!canExtract || disabled}
            onClick={onExtract}
          >
            <Sparkles className="h-4 w-4" aria-hidden="true" />
            Extract → graph
          </Button>
        ) : null}
        {disabled ? (
          <Button type="button" variant="secondary" className="gap-2" onClick={onCancel}>
            <Square className="h-4 w-4" aria-hidden="true" />
            Cancel
          </Button>
        ) : (
          <Button type="submit" className="gap-2" disabled={!question.trim()}>
            <Send className="h-4 w-4" aria-hidden="true" />
            Send
          </Button>
        )}
      </div>
    </form>
  );
}
